import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

const PAGE_FETCH_CONCURRENCY = 4;

/**
 * @typedef {Object} VirtualState
 * @property {HTMLElement[]} matches  In-stock matching cards across the entire collection
 * @property {number} total           Total in-stock matches
 */

/**
 * Custom element that renders the collection grid.
 *
 * Two modes:
 * - **Native mode** (no variant filter active): server pagination handles
 *   everything; this class only wires layout-change handling.
 * - **Virtual mode** (one or more `filter.v.option.*` params present):
 *   fetches every native page in parallel via the Section Rendering API,
 *   filters cards client-side for matching in-stock variants, and renders
 *   24 of them per virtual page. Numbered pagination links are replaced
 *   with virtual ones; clicks are intercepted and handled via pushState.
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #gridObserver;

  /** @type {boolean} */
  #internalMutation = false;

  /** @type {boolean} */
  #pendingExternal = false;

  /** @type {number | undefined} */
  #applyTimer;

  /** @type {Promise<void> | null} */
  #applyInFlight = null;

  /** @type {Map<string, VirtualState>} */
  #filterCache = new Map();

  /** @type {Map<string, Promise<VirtualState>>} */
  #buildInFlight = new Map();

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('click', this.#handleSectionClick);
    window.addEventListener('popstate', this.#handlePopState);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.setAttribute('initialized', '');

    this.#observeGrid();
    this.#scheduleApply();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('click', this.#handleSectionClick);
    window.removeEventListener('popstate', this.#handlePopState);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.#gridObserver?.disconnect();
    if (this.#applyTimer) clearTimeout(this.#applyTimer);
  }

  /**
   * Filters mutations: returns true only if any add/remove involves a node
   * we did not produce (so morph or parent infinite-scroll appends count,
   * but our own grid replacements do not).
   * @param {MutationRecord[]} mutations
   */
  #hasExternalMutation(mutations) {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node instanceof HTMLElement && !node.dataset.virtualManaged) return true;
      }
      for (const node of m.removedNodes) {
        if (node instanceof HTMLElement && !node.dataset.virtualManaged) return true;
      }
    }
    return false;
  }

  #observeGrid() {
    const { grid } = this.refs;
    if (!grid) return;
    this.#gridObserver?.disconnect();
    this.#gridObserver = new MutationObserver((mutations) => {
      if (this.#internalMutation) return;
      if (!this.#hasExternalMutation(mutations)) return;
      this.#scheduleApply();
    });
    this.#gridObserver.observe(grid, { childList: true });
  }

  /**
   * Wraps a synchronous DOM mutation so the MutationObserver callback (which
   * runs as a microtask after the current task) sees `#internalMutation=true`
   * and skips it. Reset is queued as a microtask so it runs *after* the MO
   * callback for our mutations, but before any later real external mutation.
   * @param {() => void} fn
   */
  #withInternalMutation(fn) {
    this.#internalMutation = true;
    try {
      fn();
    } finally {
      queueMicrotask(() => {
        this.#internalMutation = false;
      });
    }
  }

  #scheduleApply = () => {
    if (this.#applyTimer) clearTimeout(this.#applyTimer);
    this.#applyTimer = window.setTimeout(() => {
      this.#applyTimer = undefined;
      this.#runApply();
    }, 16);
  };

  async #runApply() {
    if (this.#applyInFlight) {
      this.#pendingExternal = true;
      return;
    }
    this.#applyInFlight = this.#applyVariantFilter().catch((err) =>
      console.warn('[results-list] variant filter failed', err)
    );
    try {
      await this.#applyInFlight;
    } finally {
      this.#applyInFlight = null;
      // Reveal the grid once the first apply has run (set by Liquid when an
      // option filter is active in the URL — see sections/main-collection.liquid).
      this.removeAttribute('data-virtual-pending');
      if (this.#pendingExternal) {
        this.#pendingExternal = false;
        this.#scheduleApply();
      }
    }
  }

  /**
   * Routes between native mode (no variant filter) and virtual mode.
   */
  async #applyVariantFilter() {
    const activeFilters = this.#readActiveOptionFilters();
    if (activeFilters.length === 0 && !this.hasAttribute('data-force-stock-filter')) {
      // No filter: server pagination is correct as-rendered. Make sure no
      // stale virtual cards remain in the grid (e.g., user just removed the
      // last filter via the back button).
      this.#clearVirtualGrid();
      return;
    }
    // With no active option filters, #cardHasMatchingAvailableVariant still
    // requires an in-stock variant, so this also covers the
    // data-force-stock-filter-only case (e.g. the "All" collection).
    await this.#renderVirtualMode(activeFilters);
  }

  /**
   * Removes any virtual cards from the grid so the server-rendered native
   * cards remain. Called when filters are cleared.
   */
  #clearVirtualGrid() {
    const { grid } = this.refs;
    if (!grid) return;
    const virtualCards = grid.querySelectorAll(':scope > [ref="cards[]"][data-virtual-managed]');
    if (virtualCards.length === 0) return;
    this.#withInternalMutation(() => {
      for (const card of virtualCards) card.remove();
    });
  }

  /**
   * @param {Array<{name: string, values: Set<string>}>} activeFilters
   */
  async #renderVirtualMode(activeFilters) {
    const filterKey = this.#filterKey();
    let state = this.#filterCache.get(filterKey);
    if (!state) {
      let pending = this.#buildInFlight.get(filterKey);
      if (!pending) {
        pending = this.#buildFilteredState(activeFilters).then((s) => {
          this.#filterCache.set(filterKey, s);
          this.#buildInFlight.delete(filterKey);
          return s;
        });
        this.#buildInFlight.set(filterKey, pending);
      }
      state = await pending;
    }
    // If the URL moved on while we were building (rapid filter clicks), bail.
    if (this.#filterKey() !== filterKey) return;
    this.#renderVirtualPage(state);
  }

  /**
   * Fetches every native server page in parallel (with a concurrency cap)
   * and builds an ordered array of cards whose variants match all active
   * option filters AND are currently in stock.
   * @param {Array<{name: string, values: Set<string>}>} activeFilters
   * @returns {Promise<VirtualState>}
   */
  async #buildFilteredState(activeFilters) {
    const { grid } = this.refs;
    const lastPage = parseInt(grid?.dataset.lastPage || '1', 10);

    const baseUrl = new URL(window.location.href);
    baseUrl.hash = '';
    baseUrl.searchParams.delete('page');

    const pageNumbers = [];
    for (let p = 1; p <= lastPage; p++) pageNumbers.push(p);

    /** @type {Map<number, HTMLElement[]>} */
    const cardsByPage = new Map();

    const fetchPage = async (p) => {
      const url = new URL(baseUrl);
      url.searchParams.set('page', String(p));
      try {
        const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
        cardsByPage.set(p, this.#extractCardsFromHTML(html));
      } catch (err) {
        console.warn('[results-list] failed to fetch page', p, err);
        cardsByPage.set(p, []);
      }
    };

    // Bounded concurrency
    const queue = pageNumbers.slice();
    const workers = [];
    const workerCount = Math.min(PAGE_FETCH_CONCURRENCY, queue.length);
    for (let w = 0; w < workerCount; w++) {
      workers.push(
        (async () => {
          while (queue.length) {
            const p = queue.shift();
            if (p === undefined) return;
            await fetchPage(p);
          }
        })()
      );
    }
    await Promise.all(workers);

    /** @type {HTMLElement[]} */
    const matches = [];
    for (let p = 1; p <= lastPage; p++) {
      const cards = cardsByPage.get(p) || [];
      for (const card of cards) {
        if (this.#cardHasMatchingAvailableVariant(card, activeFilters)) {
          matches.push(card);
        }
      }
    }

    return { matches, total: matches.length };
  }

  /**
   * Replaces the grid contents and pagination markup for the current
   * virtual page (read from `?page=N` in the URL).
   * @param {VirtualState} state
   */
  #renderVirtualPage(state) {
    const { grid } = this.refs;
    if (!grid) return;

    const target = this.#targetPerPage();
    const totalPages = Math.max(1, Math.ceil(state.total / target));
    let virtualPage = this.#currentVirtualPage();
    if (virtualPage > totalPages) virtualPage = totalPages;
    if (virtualPage < 1) virtualPage = 1;

    const start = (virtualPage - 1) * target;
    const end = start + target;
    const slice = state.matches.slice(start, end);

    const fragments = slice.map((card) => {
      const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
      clone.dataset.virtualManaged = '1';
      return clone;
    });
    this.#withInternalMutation(() => {
      grid.replaceChildren(...fragments);
    });

    this.#renderVirtualPagination(virtualPage, totalPages);
    this.#updateCountDisplay(state.total);
    this.#toggleEmptyState(state.total === 0);
  }

  /**
   * Replaces the inside of the existing `.pagination` nav with virtual links.
   * Each link's href is the same `?page=N` URL the server would have used,
   * but the click handler intercepts them so navigation stays in-page.
   * @param {number} current
   * @param {number} total
   */
  #renderVirtualPagination(current, total) {
    const nav = this.querySelector('.pagination');
    const list = nav?.querySelector('.pagination__list');
    if (!nav || !list) return;

    if (total <= 1) {
      list.innerHTML = '';
      nav.style.display = 'none';
      return;
    }
    nav.style.removeProperty('display');

    const baseUrl = new URL(window.location.href);
    baseUrl.hash = '';
    const pageHref = (n) => {
      const u = new URL(baseUrl);
      u.searchParams.set('page', String(n));
      return u.pathname + u.search;
    };

    const pages = this.#computePagesToShow(current, total);

    let html = '';
    // Prev arrow
    if (current > 1) {
      html += `<li class="pagination__item"><a href="${pageHref(current - 1)}" class="pagination__link pagination__link--arrow" data-virtual-page="${current - 1}" aria-label="Previous">‹</a></li>`;
    } else {
      html += `<li class="pagination__item"><span class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">‹</span></li>`;
    }

    // Page numbers
    for (const p of pages) {
      if (p === 'gap') {
        html += `<li class="pagination__item pagination__item--gap"><span class="pagination__link pagination__link--gap" aria-hidden="true">…</span></li>`;
      } else if (p === current) {
        html += `<li class="pagination__item"><span class="pagination__link pagination__link--page pagination__link--current" aria-current="page">${p}</span></li>`;
      } else {
        html += `<li class="pagination__item"><a href="${pageHref(p)}" class="pagination__link pagination__link--page" data-virtual-page="${p}" aria-label="Page ${p}">${p}</a></li>`;
      }
    }

    // Next arrow
    if (current < total) {
      html += `<li class="pagination__item"><a href="${pageHref(current + 1)}" class="pagination__link pagination__link--arrow" data-virtual-page="${current + 1}" aria-label="Next">›</a></li>`;
    } else {
      html += `<li class="pagination__item"><span class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">›</span></li>`;
    }

    list.innerHTML = html;
    nav.dataset.current_page = String(current);
  }

  /**
   * Builds a tidy page list with ellipses, e.g. [1, 'gap', 4, 5, 6, 'gap', 12].
   * @param {number} current
   * @param {number} total
   * @returns {Array<number | 'gap'>}
   */
  #computePagesToShow(current, total) {
    const set = new Set([1, total, current, current - 1, current + 1, current - 2, current + 2]);
    const sorted = Array.from(set).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    /** @type {Array<number | 'gap'>} */
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (prev && p - prev > 1) result.push('gap');
      result.push(p);
      prev = p;
    }
    return result;
  }

  /**
   * Updates the "X items" counter shown beside the filter UI to reflect the
   * filtered total rather than the unfiltered server count.
   * @param {number} count
   */
  #updateCountDisplay(count) {
    const wrappers = this.querySelectorAll('.products-count-wrapper > span');
    for (const span of wrappers) {
      // Replace the first digit run in-place so surrounding text and whitespace
      // (e.g. "113 artikelen", "1,234 items") are preserved.
      const original = span.textContent ?? '';
      if (/\d/.test(original)) {
        span.textContent = original.replace(/\d[\d.,]*/, String(count));
      } else {
        span.textContent = String(count);
      }
    }
  }

  /**
   * Show/hide a fallback empty state when filtering yields zero matches.
   * @param {boolean} isEmpty
   */
  #toggleEmptyState(isEmpty) {
    const { grid } = this.refs;
    const wrapper = grid?.parentElement;
    if (!wrapper) return;
    let empty = wrapper.querySelector('[data-virtual-empty]');
    if (isEmpty) {
      if (!empty) {
        empty = document.createElement('div');
        empty.className = 'main-collection-grid__empty';
        empty.setAttribute('data-virtual-empty', '');
        empty.innerHTML = '<h2 class="main-collection-grid__empty-title h2">No in-stock products match your filters.</h2>';
        wrapper.insertBefore(empty, grid);
      }
      grid.style.display = 'none';
    } else if (empty) {
      empty.remove();
      grid.style.removeProperty('display');
    }
  }

  /**
   * Intercepts clicks on virtual pagination links and navigates via pushState.
   * @param {MouseEvent} e
   */
  #handleSectionClick = (e) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = e.target instanceof Element ? e.target : null;
    const link = target?.closest('a[data-virtual-page]');
    if (!(link instanceof HTMLAnchorElement)) return;
    e.preventDefault();
    const href = link.getAttribute('href');
    if (!href) return;
    history.pushState({}, '', href);
    this.#scheduleApply();
    // Scroll grid into view for better UX, mimicking native pagination behavior
    const grid = this.refs.grid;
    if (grid) grid.scrollIntoView({ behavior: 'instant', block: 'start' });
  };

  /**
   * Re-renders the virtual page when the user uses browser back/forward.
   */
  #handlePopState = () => {
    this.#scheduleApply();
  };

  /**
   * Re-applies the variant filter when facets dispatch a FilterUpdate event.
   * Why: morph re-renders the section but if the resulting page-1 cards happen
   * to match the previous page-1 cards by id+order (e.g. most senior sticks
   * include the 37.5" size, so filtering by it preserves the top sellers),
   * the grid's childList MutationObserver never fires and the skeleton state
   * (data-virtual-pending) stays on indefinitely.
   */
  #handleFilterUpdate = () => {
    this.#scheduleApply();
  };

  /**
   * @returns {Array<{name: string, values: Set<string>}>}
   */
  #readActiveOptionFilters() {
    const url = new URL(window.location.href);
    const grouped = new Map();
    for (const [key, value] of url.searchParams.entries()) {
      const match = key.match(/^filter\.v\.option\.(.+)$/);
      if (!match) continue;
      const name = decodeURIComponent(match[1]).toLowerCase();
      if (!grouped.has(name)) grouped.set(name, new Set());
      grouped.get(name).add(value);
    }
    return Array.from(grouped, ([name, values]) => ({ name, values }));
  }

  /**
   * @param {Element} card
   * @param {Array<{name: string, values: Set<string>}>} activeFilters
   */
  #cardHasMatchingAvailableVariant(card, activeFilters) {
    if (!(card instanceof HTMLElement)) return true;
    let variants;
    try {
      variants = JSON.parse(card.dataset.variants || '[]');
    } catch {
      return true;
    }
    const optionNames = (card.dataset.optionNames || '')
      .split('|')
      .map((n) => n.toLowerCase().trim());

    return variants.some((tuple) => {
      if (!Array.isArray(tuple) || tuple.length < 4) return false;
      const [o1, o2, o3, available] = tuple;
      if (!available) return false;
      for (const filter of activeFilters) {
        const idx = optionNames.indexOf(filter.name);
        if (idx === -1) return false;
        const value = idx === 0 ? o1 : idx === 1 ? o2 : o3;
        if (value == null) return false;
        if (!filter.values.has(String(value))) return false;
      }
      return true;
    });
  }

  /**
   * @param {string} html
   */
  #extractCardsFromHTML(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = doc.querySelector('[ref="grid"]');
    if (!newGrid) return [];
    return Array.from(newGrid.querySelectorAll(':scope > [ref="cards[]"]'));
  }

  #currentVirtualPage() {
    const url = new URL(window.location.href);
    return Math.max(1, parseInt(url.searchParams.get('page') || '1', 10) || 1);
  }

  #targetPerPage() {
    const value = parseInt(this.dataset.targetPerPage || '24', 10);
    return Number.isFinite(value) && value > 0 ? value : 24;
  }

  /**
   * Cache key derived from the current filter and sort state. Excludes the
   * `page` param so that paging within the same filter reuses the cache.
   */
  #filterKey() {
    const url = new URL(window.location.href);
    const parts = [];
    for (const [k, v] of url.searchParams.entries()) {
      if (k === 'page') continue;
      parts.push(`${k}=${v}`);
    }
    parts.sort();
    return `${url.pathname}?${parts.join('&')}`;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
